/**
 * navigation.js — single source of truth for the marketing nav + footer IA.
 *
 * Scales without touching components: add an item here, it shows up in the
 * mega-menu / mobile drawer / footer. Menus link to HUBS (e.g. /blog), not
 * individual posts, so growing to 100s of pages needs zero nav changes.
 *
 * Product/feature + developer-detail links point to the rich docs.linklane.io
 * guides (real depth) rather than dead /signup links.
 */
import {
  Link2,
  BarChart3,
  Zap,
  QrCode,
  Target,
  UserSquare2,
  Sparkles,
  Crosshair,
  Megaphone,
  Globe,
  BookOpen,
  Compass,
  GitCompare,
  Trophy,
  Briefcase,
  Rocket,
  Code2,
  KeyRound,
  Webhook,
  Puzzle,
  Boxes,
  Activity,
  FileText,
  Users,
  Building2,
  Mail,
  ShieldCheck,
} from "lucide-react";

export const DOCS_URL = "https://docs.linklane.io";
const g = (p) => `${DOCS_URL}${p}`; // docs link helper

// ─── PRODUCT ──────────────────────────────────────────────────────────────────
export const PRODUCT_MENU = {
  columns: [
    {
      heading: "Core",
      items: [
        {
          icon: Link2,
          label: "Link Management",
          desc: "Branded short links, aliases, bulk",
          href: g("/docs/guides/links"),
        },
        {
          icon: BarChart3,
          label: "Analytics",
          desc: "Real-time clicks, geo, heatmaps",
          href: g("/docs/guides/analytics"),
        },
        {
          icon: Zap,
          label: "Smart Routing",
          desc: "Geo, device, A/B, rotator, time",
          href: g("/docs/guides/smart-routing"),
          badge: { tone: "candy", text: "Pro" },
        },
        {
          icon: QrCode,
          label: "QR Codes",
          desc: "Branded, trackable QR for any link",
          href: g("/docs/guides/qr-codes"),
        },
        {
          icon: Target,
          label: "Campaigns & UTM",
          desc: "Group links, auto-append UTM",
          href: g("/docs/guides/campaigns"),
        },
      ],
    },
    {
      heading: "Growth & creator",
      items: [
        {
          icon: UserSquare2,
          label: "Bio Pages",
          desc: "Link-in-bio with themes",
          href: g("/docs/guides/link-in-bio"),
        },
        {
          icon: Sparkles,
          label: "AI Features",
          desc: "Analytics intelligence, intent-to-link, copilots",
          href: g("/docs/guides/ai"),
          badge: { tone: "pink", text: "New" },
        },
        {
          icon: Crosshair,
          label: "Pixel Retargeting",
          desc: "Meta/Google/TikTok/Pinterest",
          href: g("/docs/guides/pixel-retargeting"),
          badge: { tone: "mint", text: "Growth" },
        },
        {
          icon: Megaphone,
          label: "CTA Overlays",
          desc: "Branded banner + CTA before redirect",
          href: g("/docs/guides/cta-overlays"),
          badge: { tone: "mint", text: "Growth" },
        },
        {
          icon: Globe,
          label: "Custom Domains",
          desc: "Serve links from your brand",
          href: g("/docs/guides/custom-domains"),
        },
      ],
    },
  ],
  featured: {
    eyebrow: "New here?",
    title: "What is LinkLane?",
    body: "The link platform for short links, QR, routing, bio pages & analytics.",
    to: "/what-is-linklane",
    cta: { label: "Start free", to: "/signup" },
  },
};

// ─── SOLUTIONS ──────────────────────────────────────────────────────────────────
export const SOLUTIONS_MENU = {
  columns: [
    {
      heading: "By use case",
      items: [
        {
          icon: Sparkles,
          label: "Creators",
          desc: "Bio pages + link tracking",
          to: "/use-cases/creators",
        },
        {
          icon: Briefcase,
          label: "Agencies",
          desc: "Client links at scale",
          to: "/use-cases/agencies",
        },
        { icon: Boxes, label: "SaaS", desc: "Deep links & attribution", to: "/use-cases/saas" },
        { icon: Target, label: "Events", desc: "QR + campaign tracking", to: "/use-cases/events" },
        { icon: Compass, label: "All use cases", desc: "Browse every workflow", to: "/use-cases" },
      ],
    },
    {
      heading: "By goal",
      items: [
        {
          icon: Target,
          label: "Track campaigns",
          desc: "UTM + rolled-up analytics",
          href: g("/docs/guides/campaigns"),
        },
        {
          icon: Link2,
          label: "Manage links",
          desc: "Aliases, protection, bulk",
          href: g("/docs/guides/links"),
        },
        {
          icon: UserSquare2,
          label: "Build bio pages",
          desc: "One link for everything",
          href: g("/docs/guides/link-in-bio"),
        },
        {
          icon: QrCode,
          label: "Generate QR codes",
          desc: "Branded, trackable",
          href: g("/docs/guides/qr-codes"),
        },
        {
          icon: BarChart3,
          label: "Measure performance",
          desc: "Clicks, geo, devices",
          href: g("/docs/guides/analytics"),
        },
      ],
    },
  ],
  featured: {
    eyebrow: "Compare",
    title: "LinkLane vs Bitly",
    body: "Feature-by-feature — and where each one wins.",
    to: "/compare/linklane-vs-bitly",
    cta: { label: "See all comparisons", to: "/compare" },
  },
};

// ─── DEVELOPERS ──────────────────────────────────────────────────────────────────
export const DEVELOPERS_MENU = {
  columns: [
    {
      heading: "Build",
      items: [
        {
          icon: Code2,
          label: "API Reference",
          desc: "REST endpoints + examples",
          href: g("/api/overview"),
        },
        {
          icon: KeyRound,
          label: "Authentication",
          desc: "Scoped API keys (Pro+)",
          href: g("/api/authentication"),
        },
        {
          icon: Webhook,
          label: "Webhooks",
          desc: "HMAC-signed event delivery",
          href: g("/api/webhooks"),
        },
        {
          icon: Puzzle,
          label: "Integrations",
          desc: "Slack, Discord & more",
          href: g("/api/integrations"),
        },
      ],
    },
    {
      heading: "Reference",
      items: [
        {
          icon: Boxes,
          label: "SDKs",
          desc: "Official + community libraries",
          href: g("/api/sdks"),
        },
        {
          icon: FileText,
          label: "Rate limits",
          desc: "Quotas & best practices",
          href: g("/api/rate-limits"),
        },
        { icon: BookOpen, label: "Developer docs", desc: "Guides & quickstarts", href: DOCS_URL },
        { icon: Activity, label: "Status", desc: "Live uptime & incidents", to: "/status" },
      ],
    },
  ],
  featured: {
    eyebrow: "Quickstart",
    title: "Build with the API",
    body: "Shorten your first link in a single POST request.",
    href: g("/api/overview"),
    cta: { label: "Get an API key", to: "/signup" },
  },
};

// ─── RESOURCES ──────────────────────────────────────────────────────────────────
export const RESOURCES_MENU = {
  columns: [
    {
      heading: "Learn",
      items: [
        { icon: BookOpen, label: "Blog", desc: "Tactics & playbooks", to: "/blog" },
        { icon: Compass, label: "Guides", desc: "Deep, evergreen how-tos", to: "/guides" },
        { icon: FileText, label: "Documentation", desc: "Product + API docs", href: DOCS_URL },
        {
          icon: Rocket,
          label: "Changelog",
          desc: "What's new in LinkLane",
          href: `${DOCS_URL}/changelog`,
        },
      ],
    },
    {
      heading: "Decide",
      items: [
        {
          icon: GitCompare,
          label: "Comparisons",
          desc: "LinkLane vs alternatives",
          to: "/compare",
        },
        { icon: Trophy, label: "Alternatives", desc: "Best-of roundups", to: "/alternatives" },
        { icon: Briefcase, label: "Use cases", desc: "Built for your workflow", to: "/use-cases" },
      ],
    },
  ],
  // featured cards are injected dynamically from the content registry (latest
  // blog + guide) by PlayfulNavbar; see buildResourcesFeatured().
};

// ─── COMPANY ──────────────────────────────────────────────────────────────────
export const COMPANY_MENU = {
  columns: [
    {
      heading: "Company",
      items: [
        { icon: Building2, label: "About", desc: "Why we built LinkLane", to: "/about" },
        { icon: Users, label: "Customers", desc: "Who builds on LinkLane", to: "/customers" },
        { icon: Mail, label: "Contact", desc: "Talk to us or book a demo", to: "/contact" },
        { icon: Briefcase, label: "Careers", desc: "Join the team", to: "/careers" },
      ],
    },
    {
      heading: "Trust",
      items: [
        { icon: ShieldCheck, label: "Security", desc: "How we protect your data", to: "/security" },
        { icon: FileText, label: "Privacy", desc: "Privacy policy", to: "/privacy" },
        { icon: FileText, label: "Terms", desc: "Terms of service", to: "/terms" },
        { icon: Activity, label: "Status", desc: "System uptime", to: "/status" },
      ],
    },
  ],
  featured: {
    eyebrow: "Trust",
    title: "Built for trust",
    body: "GDPR-ready, SOC 2-aligned, and transparent by default.",
    to: "/security",
  },
};

// Top-level menus, in order. Pricing is a plain link (no dropdown).
export const PRIMARY_NAV = [
  { key: "product", label: "Product", menu: PRODUCT_MENU },
  { key: "solutions", label: "Solutions", menu: SOLUTIONS_MENU },
  { key: "developers", label: "Developers", menu: DEVELOPERS_MENU },
  { key: "resources", label: "Resources", menu: RESOURCES_MENU },
  { key: "company", label: "Company", menu: COMPANY_MENU },
];

// ─── FOOTER ──────────────────────────────────────────────────────────────────
export const FOOTER_SECTIONS = [
  {
    heading: "Product",
    links: [
      { label: "AI Features", href: g("/docs/guides/ai") },
      { label: "Link Management", href: g("/docs/guides/links") },
      { label: "Bio Pages", href: g("/docs/guides/link-in-bio") },
      { label: "QR Codes", href: g("/docs/guides/qr-codes") },
      { label: "Analytics", href: g("/docs/guides/analytics") },
      { label: "Smart Routing", href: g("/docs/guides/smart-routing") },
      { label: "CTA Overlays", href: g("/docs/guides/cta-overlays") },
      { label: "Campaigns", href: g("/docs/guides/campaigns") },
    ],
  },
  {
    heading: "Developers",
    links: [
      { label: "API Reference", href: g("/api/overview") },
      { label: "Webhooks", href: g("/api/webhooks") },
      { label: "Integrations", href: g("/api/integrations") },
      { label: "SDKs", href: g("/api/sdks") },
      { label: "Documentation", href: DOCS_URL },
      { label: "Status", to: "/status" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Blog", to: "/blog" },
      { label: "Guides", to: "/guides" },
      { label: "Changelog", href: `${DOCS_URL}/changelog` },
      { label: "Comparisons", to: "/compare" },
      { label: "Alternatives", to: "/alternatives" },
      { label: "Use cases", to: "/use-cases" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Customers", to: "/customers" },
      { label: "Contact", to: "/contact" },
      { label: "Careers", to: "/careers" },
      { label: "What is LinkLane?", to: "/what-is-linklane" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
      { label: "Security", to: "/security" },
      { label: "Cookie Policy", to: "/privacy#cookies" },
      { label: "Subprocessors", to: "/subprocessors" },
    ],
  },
];
